import { zodResolver } from '@hookform/resolvers/zod'
import { forgotPasswordSchema } from '@repo/shared'
import { Button, Input } from '@repo/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Mail, Send } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import useForgotPassword from '../../lib/api/auth/forgot-password'

import type { ForgotPasswordSchema } from '@repo/shared'

import Logo from '@/assets/logo.svg?react'
import LanguageSettings from '@/components/layout/header/language-settings'
import FormControl from '@/components/shared/form-control'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/(auth)/forgot-password')({
  head: () => ({ meta: [{ title: localizedPageTitle('forgotPassword') }] }),
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  useDocumentTitle('forgotPassword')
  const { t } = useTranslation(['forgotPassword', 'login'])

  const { mutate, isPending, isSuccess, errorMessage } = useForgotPassword()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = (data: ForgotPasswordSchema) => {
    mutate(data)
  }

  return (
    <div className="flex min-h-dvh">
      <div className="bg-card flex min-h-dvh w-full flex-col justify-between border-r md:max-w-[500px]">
        <div className="border-border flex items-center gap-3 border-b md:gap-6">
          <div className="border-r p-4 md:p-6">
            <h1 className="text-base font-semibold md:text-lg">
              {t('forgotPassword:title')}
            </h1>
          </div>
          <Logo className="h-6 shrink-0 md:h-auto" />
          <div className="ml-auto [&>div]:border-r-0">
            <LanguageSettings />
          </div>
        </div>

        <div className="mx-auto mt-6 w-full px-4 md:px-6">
          {isSuccess ? (
            <div className="space-y-4 text-center">
              <div className="bg-secondary text-secondary-foreground mx-auto flex size-12 items-center justify-center rounded-full">
                <Mail className="size-5" />
              </div>
              <h2 className="text-lg font-semibold">
                {t('forgotPassword:confirmationTitle')}
              </h2>
              <p className="text-muted-foreground text-sm">
                {t('forgotPassword:confirmationMessage')}
              </p>
              <Link
                to="/login"
                className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
              >
                <ArrowLeft className="size-4" />
                {t('forgotPassword:backToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <p className="text-muted-foreground text-sm">
                {t('forgotPassword:description')}
              </p>

              <FormControl
                htmlFor="email"
                labelText={t('forgotPassword:emailLabel')}
                error={errors.email?.message}
              >
                <Input
                  id="email"
                  {...register('email')}
                  type="email"
                  placeholder="admin@fleet.com"
                  className="bg-background"
                />
              </FormControl>

              <Button
                disabled={isPending}
                isLoading={isPending}
                type="submit"
                variant="secondary"
                size="xl"
                className="border-border dark:text-primary-foreground primary-bg-gradient w-full border"
              >
                <Send className="size-4" />
                {t('forgotPassword:submitButton')}
              </Button>

              {errorMessage && (
                <p className="text-destructive text-center text-sm">
                  {errorMessage}
                </p>
              )}

              <div className="flex justify-center">
                <Link
                  to="/login"
                  className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
                >
                  <ArrowLeft className="size-4" />
                  {t('forgotPassword:backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>

        <p className="text-muted-foreground mt-auto px-4 pb-4 text-xs md:px-6 md:pb-6">
          {t('login:allRightsReserved')}
        </p>

        <div className="border-t p-4 md:p-6">
          <h2 className="text-lg font-bold md:text-xl">
            {t('login:welcomeToGroute')}
          </h2>
        </div>
      </div>

      <div className="hidden flex-1 p-4 md:flex">
        <div className="relative hidden flex-1 overflow-hidden md:flex md:items-center md:justify-center">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 h-full w-full rounded-2xl object-cover"
          >
            <source src="/animated-gradient.mp4" type="video/mp4" />
          </video>
        </div>
      </div>
    </div>
  )
}
